<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-flex tw-justify-between tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Email Templates</h1>
        <div class="tw-flex tw-gap-3">
          <base-select label="Business" placeholder="Choose a business" v-model="selectedBusinessId" :options="businesses" labelKey="name" valueKey="id" />
          <base-button
            v-if="currentTemplate !== null && currentTemplate.type === 'email'"
            size="md"
            :loading="isRendering"
            @click="renderEmail"
            :disabled="selectedEmailTemplate == null || (requireBusinessSelection == true && selectedBusinessId == null)"
            >View Sample</base-button
          >
          <base-button v-if="currentTemplate !== null && currentTemplate.type === 'sms'" :disabled="selectedBusinessId == null" size="md" @click="showSMSModal"
            >Send Sample</base-button
          >
        </div>
      </base-content>
    </div>
    <base-content class="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full">
      <div>
        <h3 class="tw-text-lg tw-font-thin">Emails to members</h3>
        <base-list-group class="tw-col-span-1 tw-mt-2">
          <base-list-item
            v-for="(template, index) in memberTemplates"
            :key="template.name"
            :showBottomBorder="index != memberTemplates.length - 1"
            :class="{
              'tw-bg-primary-50 tw-border-l-2 tw-border-primary-400 ': selectedEmailTemplate === template.name,
              'tw-rounded-t-md': index === 0,
              'tw-rounded-b-lg': index === memberTemplates.length - 1,
            }"
          >
            <div class="tw-flex-col" @click="selectEmail(template.name, true)">
              <div>{{ template.label }}</div>
              <div class="tw-text-xs tw-text-gray-500">{{ template.description }}</div>
              <base-badge v-if="selectedEmailTemplate === template.name" size="sm" class="tw-mt-3">{{ template.name }}</base-badge>
            </div>
          </base-list-item>
        </base-list-group>
        <h3 class="tw-text-lg tw-font-thin tw-mt-5">Emails to business</h3>
        <base-list-group class="tw-col-span-1 tw-mt-2">
          <base-list-item
            v-for="(template, index) in businessTemplates"
            :key="template.name"
            :showBottomBorder="index != businessTemplates.length - 1"
            :class="{
              'tw-bg-primary-50 tw-border tw-border-primary-400 ': selectedEmailTemplate === template.name,
              'tw-rounded-t-lg': index === 0,
              'tw-rounded-b-lg': index === memberTemplates.length - 1,
            }"
          >
            <div class="tw-flex-col" @click="selectEmail(template.name, false)">
              <div>{{ template.label }}</div>
              <div class="tw-text-xs tw-text-gray-500">{{ template.description }}</div>
            </div>
          </base-list-item>
        </base-list-group>
        <h3 class="tw-text-lg tw-font-thin tw-mt-5">Emails to locations</h3>
        <base-list-group class="tw-col-span-1 tw-mt-2">
          <base-list-item
            v-for="(template, index) in locationTemplates"
            :key="template.name"
            :showBottomBorder="index != locationTemplates.length - 1"
            :class="{
              'tw-bg-primary-50 tw-border tw-border-primary-400 ': selectedEmailTemplate === template.name,
              'tw-rounded-t-lg': index === 0,
              'tw-rounded-b-lg': index === memberTemplates.length - 1,
            }"
          >
            <div class="tw-flex-col" @click="selectEmail(template.name, true)">
              <div>{{ template.label }}</div>
              <div class="tw-text-xs tw-text-gray-500">{{ template.description }}</div>
            </div>
          </base-list-item>
        </base-list-group>
        <h3 class="tw-text-lg tw-font-thin tw-mt-5">Emails to Predictile</h3>
        <base-list-group class="tw-col-span-1 tw-mt-2">
          <base-list-item
            v-for="(template, index) in predictileTemplates"
            :key="template.name"
            :showBottomBorder="index != predictileTemplates.length - 1"
            :class="{
              'tw-bg-primary-50 tw-border tw-border-primary-400 ': selectedEmailTemplate === template.name,
              'tw-rounded-t-lg': index === 0,
              'tw-rounded-b-lg': index === memberTemplates.length - 1,
            }"
          >
            <div class="tw-flex-col" @click="selectEmail(template.name, false)">
              <div>{{ template.label }}</div>
              <div class="tw-text-xs tw-text-gray-500">{{ template.description }}</div>
            </div>
          </base-list-item>
        </base-list-group>
      </div>
      <div v-if="selectedEmailTemplate != null" class="tw-flex tw-flex-col tw-col-span-2">
        <h3 class="tw-text-lg tw-font-thin tw-mb-2">{{ currentTemplate.label }}</h3>
        <base-card>
          <div>
            Default content
          </div>
          <base-form ref="defaultForm" class="tw-mt-3">
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Subject"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="subject"
              size="xs"
              :savedValue="defaultSubject.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Preview"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="preview_text"
              size="xs"
              :savedValue="defaultPreview.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Headline"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="headline_text"
              size="xs"
              :savedValue="defaultHeadline.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Greeting"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="greeting_text"
              size="xs"
              :savedValue="defaultGreeting.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Body Intro"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="body_intro_text"
              size="xs"
              :savedValue="defaultBodyIntro.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
            <template-section
              label="Body Text"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="body_text"
              size="xs"
              :savedValue="defaultBody.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
              type="text-area"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Call to Action Button Text"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="cta_button_text"
              size="xs"
              :savedValue="defaultCtaText.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Call to Action Button Link"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="cta_button_link"
              size="xs"
              :savedValue="defaultCtaLink.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Footer Text"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="foot_note_text"
              size="xs"
              :savedValue="defaultFootNoteText.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Footer Link"
              :template-name="selectedEmailTemplate"
              business-id="default"
              section="foot_note_link"
              size="xs"
              :savedValue="defaultFootNoteLink.value"
              :loading="defaultSectionsLoading"
              @deleted="loadDefaultSections"
              class="tw-mt-3"
            />
          </base-form>
        </base-card>
        <base-card v-if="selectedEmailTemplate != null && selectedBusinessId != null" class="tw-mt-6">
          <div>Custom content: {{ businesses.find((x) => x.id == selectedBusinessId).name }}</div>
          <base-form ref="businessForm" class="tw-mt-3">
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Subject"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="subject"
              size="xs"
              :savedValue="businessSubject.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Preview"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="preview_text"
              size="xs"
              :savedValue="businessPreview.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Headline"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="headline_text"
              size="xs"
              :savedValue="businessHeadline.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Greeting"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="greeting_text"
              size="xs"
              :savedValue="businessGreeting.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Body Intro"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="body_intro_text"
              size="xs"
              :savedValue="businessBodyIntro.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
            <template-section
              label="Body Text"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="body_text"
              size="xs"
              :savedValue="businessBody.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
              type="text-area"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Call to Action Button Text"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="cta_button_text"
              size="xs"
              :savedValue="businessCtaText.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Call to Action Button Link"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="cta_button_link"
              size="xs"
              :savedValue="businessCtaLink.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Footer Text"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="foot_note_text"
              size="xs"
              :savedValue="businessFootNoteText.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
            <template-section
              v-if="currentTemplate !== null && currentTemplate.type === 'email'"
              label="Footer Link"
              :template-name="selectedEmailTemplate"
              :business-id="selectedBusinessId"
              section="foot_note_link"
              size="xs"
              :savedValue="businessFootNoteLink.value"
              :loading="businessSectionsLoading"
              @deleted="loadBusinessSections"
              class="tw-mt-3"
            />
          </base-form>
        </base-card>
      </div>
    </base-content>
    <view-email ref="viewEmailModal" />
    <sample-sms-modal ref="smsModal" />
  </div>
</template>

<script>
import TemplateSection from "@/views/admin/email/components/TemplateSection";
import ViewEmail from "@/views/admin/email/components/ViewEmail";
import SampleSmsModal from "@/views/admin/email/components/SampleSmsModal";

export default {
  components: { TemplateSection, ViewEmail, SampleSmsModal },
  name: "EmailTemplatesView",
  data() {
    return {
      isLoading: false,
      defaultSectionsLoading: false,
      businessSectionsLoading: false,
      isRendering: false,
      predictileTemplates: [],
      businessTemplates: [],
      locationTemplates: [],
      memberTemplates: [],
      requireBusinessSelection: true,
      selectedEmailTemplate: null,
      selectedBusinessId: null,
      businesses: [],

      defaultSubject: { value: null },
      defaultPreview: { value: null },
      defaultHeadline: { value: null },
      defaultGreeting: { value: null },
      defaultBodyIntro: { value: null },
      defaultBody: { value: null },
      defaultCtaText: { value: null },
      defaultCtaLink: { value: null },
      defaultFootNoteText: { value: null },
      defaultFootNoteLink: { value: null },

      businessSubject: { value: null },
      businessPreview: { value: null },
      businessHeadline: { value: null },
      businessGreeting: { value: null },
      businessBodyIntro: { value: null },
      businessBody: { value: null },
      businessCtaText: { value: null },
      businessCtaLink: { value: null },
      businessFootNoteText: { value: null },
      businessFootNoteLink: { value: null },
    };
  },
  computed: {
    currentTemplate() {
      if (this.selectedEmailTemplate === null) return null;
      else {
        let template = null;
        template = this.memberTemplates.find((x) => x.name === this.selectedEmailTemplate);
        if (template !== null) return template;
        template = this.businessTemplates.find((x) => x.name === this.selectedEmailTemplate);
        if (template !== null) return template;
        template = this.locationTemplates.find((x) => x.name === this.selectedEmailTemplate);
        if (template !== null) return template;
        return null;
      }
    },
  },
  created() {
    this.loadBusinesses();
    this.loadTemplates();
  },
  watch: {
    selectedBusinessId() {
      if (this.selectedEmailTemplate != null) this.loadBusinessSections();
    },
  },
  methods: {
    loadTemplates() {
      this.isLoading = true;
      this.$axios
        .get(`/admin/email/templates`)
        .then((res) => {
          this.businessTemplates = res.data.filter((t) => t.group.toLowerCase() === "business");
          this.locationTemplates = res.data.filter((t) => t.group.toLowerCase() === "location");
          this.memberTemplates = res.data.filter((t) => t.group.toLowerCase() === "member");
          this.predictileTemplates = res.data.filter((t) => t.group.toLowerCase() === "predictile");
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load templates",
            message: "We had a problem retrieving the list of templates. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    loadDefaultSections() {
      this.defaultSectionsReset();

      this.defaultSectionsLoading = true;
      this.$axios
        .get(`/admin/email/templates/${this.selectedEmailTemplate}/default`)
        .then((res) => {
          if (res.data.findIndex((d) => d.section === "subject") > -1) this.defaultSubject = res.data.find((d) => d.section === "subject");
          if (res.data.findIndex((d) => d.section === "preview_text") > -1) this.defaultPreview = res.data.find((d) => d.section === "preview_text");
          if (res.data.findIndex((d) => d.section === "headline_text") > -1) this.defaultHeadline = res.data.find((d) => d.section === "headline_text");
          if (res.data.findIndex((d) => d.section === "greeting_text") > -1) this.defaultGreeting = res.data.find((d) => d.section === "greeting_text");
          if (res.data.findIndex((d) => d.section === "body_intro_text") > -1) this.defaultBodyIntro = res.data.find((d) => d.section === "body_intro_text");
          if (res.data.findIndex((d) => d.section === "body_text") > -1) this.defaultBody = res.data.find((d) => d.section === "body_text");
          if (res.data.findIndex((d) => d.section === "cta_button_text") > -1) this.defaultCtaText = res.data.find((d) => d.section === "cta_button_text");
          if (res.data.findIndex((d) => d.section === "cta_button_link") > -1) this.defaultCtaLink = res.data.find((d) => d.section === "cta_button_link");
          if (res.data.findIndex((d) => d.section === "foot_note_text") > -1) this.defaultFootNoteText = res.data.find((d) => d.section === "foot_note_text");
          if (res.data.findIndex((d) => d.section === "foot_note_link") > -1) this.defaultFootNoteLink = res.data.find((d) => d.section === "foot_note_link");
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load default email content",
            message: "We had a problem retrieving the default content sections for the email template. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.defaultSectionsLoading = false;
        });
    },
    loadBusinessSections() {
      this.businessSectionsReset();

      this.businessSectionsLoading = true;
      this.$axios
        .get(`/admin/email/templates/${this.selectedEmailTemplate}/custom/${this.selectedBusinessId}`)
        .then((res) => {
          if (res.data.findIndex((d) => d.section === "subject") > -1) this.businessSubject = res.data.find((d) => d.section === "subject");
          if (res.data.findIndex((d) => d.section === "preview_text") > -1) this.businessPreview = res.data.find((d) => d.section === "preview_text");
          if (res.data.findIndex((d) => d.section === "headline_text") > -1) this.businessHeadline = res.data.find((d) => d.section === "headline_text");
          if (res.data.findIndex((d) => d.section === "greeting_text") > -1) this.businessGreeting = res.data.find((d) => d.section === "greeting_text");
          if (res.data.findIndex((d) => d.section === "body_intro_text") > -1) this.businessBodyIntro = res.data.find((d) => d.section === "body_intro_text");
          if (res.data.findIndex((d) => d.section === "body_text") > -1) this.businessBody = res.data.find((d) => d.section === "body_text");
          if (res.data.findIndex((d) => d.section === "cta_button_text") > -1) this.businessCtaText = res.data.find((d) => d.section === "cta_button_text");
          if (res.data.findIndex((d) => d.section === "cta_button_link") > -1) this.businessCtaLink = res.data.find((d) => d.section === "cta_button_link");
          if (res.data.findIndex((d) => d.section === "foot_note_text") > -1) this.businessFootNoteText = res.data.find((d) => d.section === "foot_note_text");
          if (res.data.findIndex((d) => d.section === "foot_note_link") > -1) this.businessFootNoteLink = res.data.find((d) => d.section === "foot_note_link");
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load custom email content",
            message: "We had a problem retrieving the custom content sections for the email template. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.businessSectionsLoading = false;
        });
    },
    selectEmail(templateName, requireBusinessSelection) {
      this.requireBusinessSelection = requireBusinessSelection;
      this.selectedEmailTemplate = templateName;
      this.loadDefaultSections();
      if (this.selectedBusinessId != null) this.loadBusinessSections();
    },
    loadBusinesses() {
      this.$axios
        .get(`/businesses/list`)
        .then((res) => {
          if (res.data.length > 0) this.businesses = res.data.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load businesses",
            message: "We had a problem retrieving the list of businesses. Please reload the page to try again.",
            error: err,
          });
        });
    },
    defaultSectionsReset() {
      this.defaultSubject = { value: null };
      this.defaultPreview = { value: null };
      this.defaultHeadline = { value: null };
      this.defaultGreeting = { value: null };
      this.defaultBodyIntro = { value: null };
      this.defaultBody = { value: null };
      this.defaultCtaText = { value: null };
      this.defaultCtaLink = { value: null };
      this.defaultFootNoteText = { value: null };
      this.defaultFootNoteLink = { value: null };
    },
    businessSectionsReset() {
      this.businessSubject = { value: null };
      this.businessPreview = { value: null };
      this.businessHeadline = { value: null };
      this.businessGreeting = { value: null };
      this.businessBodyIntro = { value: null };
      this.businessBody = { value: null };
      this.businessCtaText = { value: null };
      this.businessCtaLink = { value: null };
      this.businessFootNoteText = { value: null };
      this.businessFootNoteLink = { value: null };
    },
    renderEmail() {
      this.isRendering = true;

      if (this.selectedBusinessId !== null) {
        this.$axios
          .get(`/admin/email/templates/${this.selectedEmailTemplate}/business/${this.selectedBusinessId}/render`)
          .then((res) => {
            this.$refs.viewEmailModal.show(res.data.subject, res.data.preview, res.data.htmlResponse);
          })
          .catch((err) => {
            this.EventBus.$emit("GLOBAL_ERROR", {
              title: "Failed to render content",
              message: "We had a problem rendering the email. Please reload the page to try again.",
              error: err,
            });
          })
          .finally(() => {
            this.isRendering = false;
          });
      } else {
        this.$axios
          .get(`/admin/email/templates/${this.selectedEmailTemplate}/render`)
          .then((res) => {
            this.$refs.viewEmailModal.show(res.data.subject, res.data.preview, res.data.htmlResponse);
          })
          .catch((err) => {
            this.EventBus.$emit("GLOBAL_ERROR", {
              title: "Failed to render content",
              message: "We had a problem rendering the email. Please reload the page to try again.",
              error: err,
            });
          })
          .finally(() => {
            this.isRendering = false;
          });
      }
    },
    showSMSModal() {
      this.$refs.smsModal.show(this.selectedBusinessId, this.selectedEmailTemplate);
    },
  },
};
</script>
